<template>
  <div class="payment" data-testid="customer-card-payment-form">
    <div ref="card"></div>
    <div v-if="stripeError" class="payment__error">
      <p class="payment__error--text">
        {{ stripeError }}
      </p>
    </div>
    <form v-if="!hideCardForm" @submit.prevent="submitFormToCreateToken">
      <el-row :gutter="20">
        <el-col :lg="handleNameCol.lg"
                :md="handleNameCol.md"
                :sm="handleNameCol.sm"
                :xs="handleNameCol.xs">
          <label v-if="!hideLabel"
                 :for="`card-name${suffixId}`">{{ $t('utils.cardPayment.name') }}</label>
          <el-input :id="`card-name${suffixId}`"
                    :placeholder="$t(`${sourcePlaceholder}.name`)"
                    class="payment__name__input"
                    v-model="name" />
        </el-col>
        <el-col :lg="handleNumberCol.lg"
                :md="handleNumberCol.md"
                :sm="handleNumberCol.sm"
                :xs="handleNumberCol.xs">
          <label v-if="!hideLabel"
                :for="`card-number${suffixId}`">{{ $t('utils.cardPayment.cardNumber') }}</label>
          <div :id="`card-number${suffixId}`"
               :class="['el-input__inner', { 'parsley-error': cardNumberError }]">
          </div>
          <span v-if="cardNumberError"
                class="payment__help-block">
            {{ cardNumberError }}
          </span>
        </el-col>
        <el-col class="payment__expiry"
                :lg="handleExpiryCol.lg"
                :md="handleExpiryCol.md"
                :sm="handleExpiryCol.sm"
                :xs="handleExpiryCol.xs">
          <label v-if="!hideLabel"
                 :for="`card-expiry${suffixId}`">{{ $t('utils.cardPayment.expiration') }}</label>
          <div :id="`card-expiry${suffixId}`"
               :class="['el-input__inner', { 'parsley-error': cardExpiryError }]">
          </div>
          <span v-if="cardExpiryError"
                class="payment__help-block">
              {{ cardExpiryError }}
            </span>
        </el-col>
        <el-col class="payment__cvc"
                :lg="handleCvcCol.lg"
                :md="handleCvcCol.md"
                :sm="handleCvcCol.sm"
                :xs="handleCvcCol.xs">
          <label v-if="!hideLabel"
                 :for="`card-cvc${suffixId}`">{{ $t('utils.cardPayment.cvc') }}</label>
          <div :id="`card-cvc${suffixId}`"
               :class="['el-input__inner', { 'parsley-error': cardCvcError }]">
          </div>
          <span v-if="cardCvcError"
                class="payment__help-block">
            {{ cardCvcError }}
          </span>
        </el-col>
      </el-row>
    </form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useComparatorStore } from '@/stores';

const STRIPE_TOKEN = import.meta.env.VITE_APP_STRIPE;
const valueColDefault = { xs: 24, sm: 12, lg: 6 };

export default {
  name: 'cardPayment',
  emits: ['update:loading', 'token-change', 'payment-error', 'payment-authenticated'],
  props: {
    suffixId: {
      default: '',
      type: String,
    },
    hideCardForm: {
      default: false,
      type: Boolean,
    },
    hideLabel: {
      default: false,
      type: Boolean,
    },
    nameCol: {
      type: Object,
    },
    numberCol: {
      type: Object,
    },
    cvcCol: {
      type: Object,
    },
    expiryCol: {
      type: Object,
    },
    billingAddress: {
      type: Object,
      default: null,
    },
    sourcePlaceholder: {
      type: String,
      default: 'utils.cardPayment.placeholder',
    },
  },
  data() {
    return {
      name: '',
      card: {
        cvc: null,
        number: null,
        expiry: null,
      },
      // elements
      cardNumber: '',
      cardExpiry: '',
      cardCvc: '',
      stripe: null,
      // errors
      stripeError: '',
      cardCvcError: '',
      cardExpiryError: '',
      cardNumberError: '',
    };
  },
  computed: {
    ...mapState(useComparatorStore, ['stripeToken']),
    handleExpiryCol() {
      return {
        ...valueColDefault,
        ...this.expiryCol,
      };
    },
    handleNumberCol() {
      return {
        ...valueColDefault,
        ...this.numberCol,
      };
    },
    handleCvcCol() {
      return {
        ...valueColDefault,
        ...this.cvcCol,
      };
    },
    handleNameCol() {
      return {
        ...valueColDefault,
        ...this.nameCol,
      };
    },
  },
  methods: {
    ...mapActions(useComparatorStore, ['getStripeToken']),
    setUpStripe() {
      if (window.Stripe) {
        const stripe = window.Stripe(STRIPE_TOKEN);
        this.stripe = stripe;
        if (!this.hideCardForm) {
          const style = {
            invalid: {
              color: 'var(--nxt-next-danger)',
            },
          };
          const elements = stripe.elements();

          // Create elements for card number, expiry, and cvc fields
          for (const field of [
            { type: 'cardCvc', mountId: 'cvc' },
            { type: 'cardExpiry', mountId: 'expiry' },
            { type: 'cardNumber', mountId: 'number' },
          ]) {
            const {type, mountId} = field;
            // Get placeholder from i18n
            const placeholder = this.$te(`${this.sourcePlaceholder}.${mountId}`) ? this.$t(`${this.sourcePlaceholder}.${mountId}`) : undefined;
            this[type] = elements.create(type, { style, placeholder });
            // Mount element to DOM
            this[type].mount(`#card-${mountId}${this.suffixId}`);
          }

          this.listenForErrors();
        }
      }
    },
    listenForErrors() {
      const vm = this;
      this.cardNumber.addEventListener('change', (event) => {
        vm.toggleError(event);
        vm.cardNumberError = '';
        vm.card.number = !!event.complete;
      });

      this.cardExpiry.addEventListener('change', (event) => {
        vm.toggleError(event);
        vm.cardExpiryError = '';
        vm.card.expiry = !!event.complete;
      });

      this.cardCvc.addEventListener('change', (event) => {
        vm.toggleError(event);
        vm.cardCvcError = '';
        vm.card.cvc = !!event.complete;
      });
    },
    toggleError(event) {
      if (event.error) {
        this.stripeError = event.error.message;
      } else {
        this.stripeError = '';
      }
    },
    submitFormToCreateToken() {
      this.clearCardErrors();
      let valid = true;

      if (!this.card.number) {
        valid = false;
        this.cardNumberError = this.$t('utils.cardPayment.errors.cardNumberError');
      }
      if (!this.card.cvc) {
        valid = false;
        this.cardCvcError = this.$t('utils.cardPayment.errors.cardCvcError');
      }
      if (!this.card.expiry) {
        valid = false;
        this.cardExpiryError = this.$t('utils.cardPayment.errors.cardExpiryError');
      }
      if (this.stripeError) {
        valid = false;
      }
      if (valid) {
        this.createToken();
      } else {
        this.$emit('update:loading', false);
      }
    },
    async createToken() {
      const { setupIntent, error } = await this.stripe.confirmCardSetup(
        this.stripeToken, {
          payment_method: {
            card: this.cardNumber,
            billing_details: {
              name: this.name,
              address: {
                city: this.billingAddress?.city,
                line1: this.billingAddress?.street,
                postal_code: this.billingAddress?.postal,
                country: this.billingAddress?.country_code || 'FR',
              },
            },
          },
        },
      );
      if (error) {
        this.stripeError = error.message;
        this.$emit('update:loading', false);
      } else {
        this.$emit('token-change', setupIntent.id);
      }
    },
    async authenticatePaymentError(clientSecret, paymentMethodId) {
      if (this.stripe?.confirmCardPayment) {
        const { paymentIntent, error } = await this.stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId,
        });
        if (error) {
          this.stripeError = error.message;
          this.$emit('payment-error', error);
          this.$emit('update:loading', false);
        } else if (paymentIntent.status === 'succeeded'
            || paymentIntent.status === 'requires_capture') {
          this.$emit('payment-authenticated', paymentIntent);
        }
      }
    },
    async authenticatePayment(intent) {
      this.authenticatePaymentError(
        intent.client_secret,
        intent.last_payment_error.payment_method.id,
      );
    },
    clearCardErrors() {
      this.stripeError = '';
      this.cardCvcError = '';
      this.cardExpiryError = '';
      this.cardNumberError = '';
    },
  },
  created() {
    try {
      this.getStripeToken();
    // eslint-disable-next-line no-empty
    } catch (e) {}
  },
  mounted() {
    this.setUpStripe();
  },
};

</script>

<style lang="scss" scoped>
:deep(.el-input__wrapper), .el-input__inner {
  padding-left: 15px;
  padding-right: 15px;
}

.el-input__inner {
  border: 1px solid #DCDFE6;
  border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.payment {
  @include breakpoint('mobile') {
    .el-row {
      row-gap: 20px;
    }
  }
  &__error {
    &--text {
      color: var(--nxt-color-danger);
      font-size: 16px;
      font-weight: 600;
    }
  }
  label {
    display: inline-block;
    padding: 6px 0 2px 4px;
    font-style: italic;
  }

  &__name {
    &__input {
      :deep(.el-input__inner) {
        color: var(--nxt-color-black);
        line-height: 16.8px;
        font-weight: 400;
        font-family: sans-serif;
      }

      :deep(.el-input__inner:focus) {
        border-color: var(--nxt-color-input-hover);
      }

      :deep(.el-input__inner::placeholder) {
        color: var(--nxt-color-card-placeholder);
      }
    }
  }

  &__help-block {
    color: var(--nxt-color-danger);
    font-size: 16px;
    font-weight: 500;
    margin-left: 16px;
  }

  &__submit {
    min-width: 65px;
    min-height: 34px;
  }

  .spin {
    animation: fa-spin 2s infinite linear;
  }
}
</style>
